import { getModuleSlugsFromJwtToken } from '../../utils/entitlements';
import { clearChatStorage } from '../ai-explorer/chatStorage';

const SESSION_EXPIRED_EVENT = 'saf3ai:session-expired';
const SESSION_CONTINUED_EVENT = 'saf3ai:session-continued';

/**
 * Keep localStorage.user.modules in step with a freshly refreshed JWT.
 * getEffectiveOrgModuleSlugs() already prefers the JWT claim, so this isn't
 * required for entitlement checks — but other code may read user.modules
 * directly, and leaving it stale after a token refresh is how a Super
 * Admin's module change silently failed to show up mid-session.
 */
const syncStoredUserModulesFromJwt = (token) => {
  const modules = getModuleSlugsFromJwtToken(token);
  if (!Array.isArray(modules)) return;
  try {
    const stored = JSON.parse(localStorage.getItem('user') || '{}');
    if (stored && typeof stored === 'object') {
      localStorage.setItem('user', JSON.stringify({ ...stored, modules }));
    }
  } catch {
    // Malformed stored user — leave it alone rather than clobber it.
  }
};

let sessionExpiryPromptOpen = false;

/**
 * Decide whether the session is *actually* over.
 *
 * Every API client calls emitSessionExpired() on ANY 401. But a 401 from a
 * single microservice while the JWT is still valid is almost always a
 * per-service authorization/config problem (or a transient blip) — NOT an
 * expired session. Pages that fan out to several services at once (e.g.
 * Integrations, which hits management + alert + ops + gateway) would otherwise
 * pop the "Session expired" modal off one unrelated 401 while the user is still
 * logged in. So we only treat a 401 as session expiry when the token is missing
 * or genuinely past its `exp`.
 */
const isSessionActuallyExpired = () => {
  let token;
  try {
    token = localStorage.getItem('jwt_token');
  } catch {
    // localStorage unavailable — don't tear down the session on a stray 401.
    return false;
  }
  if (!token) return true; // no token at all → genuinely unauthenticated

  try {
    const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
    const payload = JSON.parse(atob(b64));
    // No usable exp claim → can't prove it's expired, so assume still valid.
    if (!payload || typeof payload.exp !== 'number') return false;
    // exp is in seconds. Consider expired only once we're within 10s of it
    // (small skew so genuine expiry still prompts promptly).
    return Date.now() >= payload.exp * 1000 - 10_000;
  } catch {
    // Unparseable token → don't show the modal for an unrelated 401.
    return false;
  }
};

export const emitSessionExpired = (reason = 'Your session has expired. Please continue or sign in again.') => {
  if (typeof window === 'undefined' || sessionExpiryPromptOpen) return;

  if (!isSessionActuallyExpired()) {
    // Valid JWT but a service returned 401 → per-request auth failure, not a
    // session expiry. Suppress the modal so it stops popping up spuriously
    // (e.g. on the Integrations page).
    console.warn(
      '[session] Suppressed session-expiry prompt: JWT still valid — likely a service-level 401, not an expired session.',
      reason
    );
    return;
  }

  sessionExpiryPromptOpen = true;
  window.dispatchEvent(
    new CustomEvent(SESSION_EXPIRED_EVENT, {
      detail: { reason },
    })
  );
};

export const dismissSessionExpiryPrompt = () => {
  sessionExpiryPromptOpen = false;
};

export const clearSessionAndRedirectToLogin = () => {
  try {
    localStorage.removeItem('jwt_token');
    localStorage.removeItem('refresh_token');
    sessionStorage.removeItem('refresh_token');
    // The next sign-in on this browser may be someone else.
    clearChatStorage();
  } catch (error) {
    console.warn('Failed to fully clear auth storage:', error);
  } finally {
    dismissSessionExpiryPrompt();
    if (typeof window !== 'undefined' && window.location.pathname !== '/') {
      window.location.href = '/';
    }
  }
};

export const continueSession = async () => {
  const refreshToken =
    localStorage.getItem('refresh_token') || sessionStorage.getItem('refresh_token');

  if (!refreshToken) {
    return false;
  }

  try {
    const response = await fetch('/auth/api/v1/auth/refresh', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    if (!response.ok) {
      return false;
    }

    const data = await response.json();
    if (!data?.token) {
      return false;
    }

    localStorage.setItem('jwt_token', data.token);
    if (data.refresh_token) {
      localStorage.setItem('refresh_token', data.refresh_token);
    }
    syncStoredUserModulesFromJwt(data.token);
    return true;
  } catch (error) {
    console.warn('Failed to continue session:', error);
    return false;
  }
};

export const emitSessionContinued = () => {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(SESSION_CONTINUED_EVENT));
};

export { SESSION_EXPIRED_EVENT, SESSION_CONTINUED_EVENT };
